<template>
  <div class="fixed bottom-6 right-6 z-50 font-sans">
    <!-- Toggle button -->
    <button
      class="ai-toggle w-14 h-14 rounded-full bg-gradient-to-br from-teal-600 to-purple-500 text-white border-0 cursor-pointer flex items-center justify-center relative transition-transform duration-200 hover:scale-105"
      @click="isOpen = !isOpen"
      :title="isOpen ? $t('ai.widget.close') : $t('ai.widget.title')"
    >
      <svg
        v-if="!isOpen"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M12 2a8 8 0 0 0-8 8c0 3.5 2.1 6.4 5 7.7V22l3-3 3 3v-4.3c2.9-1.3 5-4.2 5-7.7a8 8 0 0 0-8-8z"
        />
        <circle cx="10" cy="10" r="1" />
        <circle cx="14" cy="10" r="1" />
      </svg>
      <svg
        v-else
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
      <span
        v-if="unreadAlerts > 0 && !isOpen"
        class="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold min-w-5 h-5 rounded-full flex items-center justify-center px-1"
      >
        {{ unreadAlerts }}
      </span>
    </button>

    <!-- Chat panel -->
    <div
      v-if="isOpen"
      class="ai-panel absolute right-0 bg-white rounded-2xl flex flex-col overflow-hidden"
      :style="panelStyle"
    >
      <!-- Report form overlay -->
      <ai-report-form
        v-if="reportFormOpen"
        :form-data="reportFormData"
        :submitting="reportFormSubmitting"
        :error="reportFormError"
        :success="reportFormSuccess"
        @close="closeReportForm"
        @submit="submitReport"
      />

      <!-- Header -->
      <div
        class="flex-shrink-0 px-4 py-3 bg-gradient-to-br from-teal-600 to-purple-500 text-white flex justify-between items-center"
      >
        <div class="flex items-center gap-2.5">
          <div
            class="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center flex-shrink-0"
          >
            <svg
              v-if="tab === 'chat'"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M12 2a8 8 0 0 0-8 8c0 3.5 2.1 6.4 5 7.7V22l3-3 3 3v-4.3c2.9-1.3 5-4.2 5-7.7a8 8 0 0 0-8-8z"
              />
            </svg>
            <svg
              v-else-if="tab === 'alerts'"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div>
            <h3 class="m-0 text-sm font-semibold leading-tight">
              {{ $t("ai.widget.title") }}
            </h3>
            <span class="text-xs opacity-75 block truncate max-w-xs">
              <template v-if="tab === 'chat'">{{
                activeConversationTitle || hostName
              }}</template>
              <template v-else-if="tab === 'alerts'">{{
                unreadAlerts > 0
                  ? $t("ai.alerts.activeAlertsCount", { count: unreadAlerts })
                  : hostName
              }}</template>
              <template v-else>{{ $t("ai.widget.tabReports") }}</template>
            </span>
          </div>
        </div>
        <div class="flex gap-1">
          <template v-if="tab === 'chat'">
            <button
              @click="newConversation"
              class="w-8 h-8 rounded-lg bg-white bg-opacity-10 border border-white border-opacity-20 text-white cursor-pointer flex items-center justify-center transition-colors hover:bg-opacity-30"
              :title="$t('ai.widget.newConversation')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            <button
              @click="toggleHistory"
              :class="
                view === 'history'
                  ? 'bg-white bg-opacity-30 border-white border-opacity-30'
                  : 'bg-white bg-opacity-10 border-white border-opacity-20 hover:bg-opacity-30'
              "
              class="w-8 h-8 rounded-lg border text-white cursor-pointer flex items-center justify-center transition-colors"
              :title="$t('ai.widget.history')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </button>
          </template>
          <button
            @click="isExpanded = !isExpanded"
            class="w-8 h-8 rounded-lg bg-white bg-opacity-10 border border-white border-opacity-20 text-white cursor-pointer flex items-center justify-center transition-colors hover:bg-opacity-30"
            :title="
              isExpanded ? $t('ai.widget.collapse') : $t('ai.widget.expand')
            "
          >
            <!-- Collapse icon -->
            <svg
              v-if="isExpanded"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="4 14 10 14 10 20" />
              <polyline points="20 10 14 10 14 4" />
              <line x1="10" y1="14" x2="3" y2="21" />
              <line x1="21" y1="3" x2="14" y2="10" />
            </svg>
            <!-- Expand icon -->
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Tab bar -->
      <div class="flex-shrink-0 flex border-b border-gray-100 bg-white">
        <button
          @click="tab = 'chat'"
          :class="
            tab === 'chat'
              ? 'text-indigo-600 border-b-2 border-indigo-500 bg-white'
              : 'text-gray-500 border-b-2 border-transparent hover:text-gray-700 hover:bg-gray-50'
          "
          class="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold transition-colors cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            />
          </svg>
          {{ $t("ai.widget.tabChat") }}
        </button>
        <button
          @click="tab = 'alerts'"
          :class="
            tab === 'alerts'
              ? 'text-indigo-600 border-b-2 border-indigo-500 bg-white'
              : 'text-gray-500 border-b-2 border-transparent hover:text-gray-700 hover:bg-gray-50'
          "
          class="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold transition-colors cursor-pointer relative"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          {{ $t("ai.widget.tabAlerts") }}
          <span
            v-if="unreadAlerts > 0"
            class="ml-0.5 bg-red-500 text-white text-xs font-bold min-w-4 h-4 rounded-full flex items-center justify-center px-1 leading-none"
            >{{ unreadAlerts }}</span
          >
        </button>
        <button
          @click="tab = 'reports'"
          :class="
            tab === 'reports'
              ? 'text-indigo-600 border-b-2 border-indigo-500 bg-white'
              : 'text-gray-500 border-b-2 border-transparent hover:text-gray-700 hover:bg-gray-50'
          "
          class="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold transition-colors cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          {{ $t("ai.widget.tabReports") }}
        </button>
      </div>

      <!-- Alerts tab -->
      <ai-alert-panel
        v-if="tab === 'alerts'"
        :pbx-host-name="hostName"
        :api-base-url="apiBaseUrl"
        :bearer-token="bearerToken"
        :embedded="true"
        @alert-count="unreadAlerts = $event"
      />

      <!-- Reports tab -->
      <ai-reports-tab
        v-if="tab === 'reports'"
        :reports="reports"
        :loading="reportsLoading"
        :delete-confirm="reportDeleteConfirm"
        :language="language"
        @refresh="fetchReports"
        @delete="deleteReport"
        @update:delete-confirm="reportDeleteConfirm = $event"
      />

      <!-- Chat tab -->
      <template v-if="tab === 'chat'">
        <!-- History view -->
        <ai-chat-history
          v-if="view === 'history'"
          :conversations="conversations"
          :loading="historyLoading"
          :conversation-id="conversationId"
          @load-conversation="loadConversation"
          @delete-conversation="deleteConversation"
          @close="view = 'chat'"
        />

        <!-- Chat view -->
        <template v-else>
          <div
            class="flex-1 overflow-y-auto p-4 chat-scroll"
            ref="messagesContainer"
          >
            <!-- Welcome -->
            <div v-if="messages.length === 0" class="py-5 px-1">
              <div class="text-center mb-5">
                <div
                  class="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-2.5"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#6366f1"
                    stroke-width="1.5"
                  >
                    <path
                      d="M12 2a8 8 0 0 0-8 8c0 3.5 2.1 6.4 5 7.7V22l3-3 3 3v-4.3c2.9-1.3 5-4.2 5-7.7a8 8 0 0 0-8-8z"
                    />
                  </svg>
                </div>
                <p class="text-sm text-gray-500 leading-relaxed m-0">
                  {{ $t("ai.widget.welcomeMessage") }}
                </p>
              </div>
              <div class="space-y-3.5">
                <div v-for="cat in suggestions" :key="cat.label">
                  <p
                    class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5 px-1"
                  >
                    {{ cat.label }}
                  </p>
                  <div class="flex flex-col gap-1">
                    <button
                      v-for="s in cat.items"
                      :key="s"
                      @click="sendMessage(s)"
                      class="w-full text-left bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm cursor-pointer text-gray-600 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 transition-colors leading-snug"
                    >
                      {{ s }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Messages -->
            <ai-chat-message
              v-for="(msg, idx) in messages"
              :key="idx"
              :msg="msg"
              :idx="idx"
              @send-message="sendMessage"
              @generate-summary="
                (prompt) => sendMessage(prompt, { isSummary: true })
              "
              @open-report-form="openReportForm"
              @new-conversation="newConversation"
            />
          </div>

          <!-- Input bar -->
          <div
            class="flex-shrink-0 flex items-center px-3.5 py-3 border-t border-gray-100 gap-2 bg-white"
          >
            <input
              v-model="inputText"
              @keyup.enter="sendMessage()"
              :placeholder="inputPlaceholder"
              :disabled="isLoading || isLimitReached"
              ref="chatInput"
              class="flex-1 border border-gray-200 rounded-full px-4 py-2.5 text-sm outline-none bg-gray-50 transition-colors focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
            />
            <button
              @click="sendMessage()"
              :disabled="isLoading || isLimitReached || !inputText.trim()"
              :class="
                isLoading || isLimitReached || !inputText.trim()
                  ? 'opacity-40 cursor-not-allowed'
                  : 'hover:scale-105 cursor-pointer'
              "
              class="w-10 h-10 flex-shrink-0 rounded-full bg-gradient-to-br from-teal-600 to-purple-500 text-white border-0 flex items-center justify-center transition-transform"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import AiAlertPanel from "./AiAlertPanel.vue";
import AiChatHistory from "./AiChatHistory.vue";
import AiChatMessage from "./AiChatMessage.vue";
import AiReportForm from "./AiReportForm.vue";
import AiReportsTab from "./AiReportsTab.vue";

const redirectionUrls = {
  get_extension_kpi: "/expert-statistics-answered-unanswered/extension",
  get_queue_kpi: "/expert-statistics-answered-unanswered/queue",
  get_queue_report: "/expert-statistics/full-report/queue",
  get_user_report: "/expert-statistics/report/extension",
  get_did_report: "/expert-statistics/did-report",
  get_callers_report: "/expert-statistics/caller-numbers-report/caller",
  get_monthly_trend: "/dashboard/trend-analysis",
  get_unique_calls: "/dashboard",
  get_agent_status: "/expert-statistics/report/extension",
  get_agent_performance: "/expert-statistics/report/extension",
};

// Maps each tool to the urlType expected by the destination view's mounted() check.
// Dispatching pbxElementType before navigation prevents the view from clearing pbxElements.
const toolElementTypes = {
  get_extension_kpi: "extension",
  get_queue_kpi: "queue",
  get_queue_report: "queue",
  get_user_report: "extension",
  get_callers_report: "caller",
  get_agent_status: "extension",
  get_agent_performance: "extension",
};

export default {
  name: "AiChatWidget",
  components: {
    AiAlertPanel,
    AiChatHistory,
    AiChatMessage,
    AiReportForm,
    AiReportsTab,
  },

  data() {
    return {
      redirectionUrls,
      isOpen: false,
      isExpanded: false,
      tab: "chat", // 'chat' | 'alerts' | 'reports'
      view: "chat", // 'chat' | 'history'
      messages: [],
      inputText: "",
      isLoading: false,
      conversationId: null,
      unreadAlerts: 0,
      conversations: [],
      historyLoading: false,
      // Report form
      reportFormOpen: false,
      reportFormData: {},
      reportFormSubmitting: false,
      reportFormError: null,
      reportFormSuccess: false,
      // Reports list
      reports: [],
      reportsLoading: false,
      reportDeleteConfirm: null,
    };
  },

  computed: {
    ...mapGetters(["hostName", "language"]),

    panelStyle() {
      if (this.isExpanded) {
        return {
          bottom: "70px",
          width: "70vh",
          height: "90vh",
          maxHeight: "960px",
          minHeight: "560px",
          transition: "width 0.25s ease, height 0.25s ease",
        };
      }
      return {
        bottom: "70px",
        width: "45vh",
        height: "80vh",
        maxHeight: "720px",
        minHeight: "420px",
        transition: "width 0.25s ease, height 0.25s ease",
      };
    },

    apiBaseUrl() {
      return localStorage.getItem("apiUrl") || "";
    },
    bearerToken() {
      return localStorage.getItem("apiToken") || "";
    },

    inputPlaceholder() {
      return this.$t("ai.widget.inputPlaceholder");
    },

    isLimitReached() {
      const last = this.messages[this.messages.length - 1];
      return last?.parsed?.type === "limit_reached";
    },

    activeConversationTitle() {
      if (!this.conversationId || !this.conversations.length) return null;
      return (
        this.conversations.find((c) => c.uuid === this.conversationId)?.title ||
        null
      );
    },

    suggestions() {
      return [
        {
          label: this.$t("ai.widget.suggest.abandonLabel"),
          items: [
            this.$t("ai.widget.suggest.abandon1"),
            this.$t("ai.widget.suggest.abandon2"),
            this.$t("ai.widget.suggest.abandon3"),
          ],
        },
        {
          label: this.$t("ai.widget.suggest.notAnsweredLabel"),
          items: [
            this.$t("ai.widget.suggest.notAnswered1"),
            this.$t("ai.widget.suggest.notAnswered2"),
          ],
        },
        {
          label: this.$t("ai.widget.suggest.comparisonLabel"),
          items: [
            this.$t("ai.widget.suggest.comparison1"),
            this.$t("ai.widget.suggest.comparison2"),
          ],
        },
        {
          label: this.$t("ai.widget.suggest.peakLabel"),
          items: [
            this.$t("ai.widget.suggest.peak1"),
            this.$t("ai.widget.suggest.peak2"),
          ],
        },
        {
          label: this.$t("ai.widget.suggest.agentLabel"),
          items: [
            this.$t("ai.widget.suggest.agent1"),
            this.$t("ai.widget.suggest.agent2"),
          ],
        },
        {
          label: this.$t("ai.widget.suggest.diagnosticLabel"),
          items: [
            this.$t("ai.widget.suggest.diagnostic1"),
            this.$t("ai.widget.suggest.diagnostic2"),
          ],
        },
      ];
    },
  },

  methods: {
    async sendMessage(text = null, { isSummary = false } = {}) {
      const message = text || this.inputText.trim();
      if (!message) return;

      this.inputText = "";
      this.messages.push({ role: "user", content: message });

      const loadingIdx = this.messages.length;
      this.messages.push({
        role: "assistant",
        loading: true,
        content: "",
        parsed: null,
      });
      this.isLoading = true;
      this.scrollToBottom();

      try {
        const response = await axios.post(
          `${this.apiBaseUrl}/api/v1.2/${this.hostName}/ai/chat`,
          {
            message,
            conversation_id: this.conversationId,
            language: this.language,
            ...(isSummary ? { is_summary: true } : {}),
          },
          {
            headers: { Authorization: `Bearer ${this.bearerToken}` },
            timeout: 120000,
          },
        );
        const data = response.data;
        this.conversationId =
          data.conversation_id || data.uuid || this.conversationId;
        this.messages[loadingIdx] = {
          role: "assistant",
          loading: false,
          content: JSON.stringify(data.response),
          parsed: data.response,
        };
        this.handleRedirection(data.usage);
      } catch (error) {
        console.error("AI Chat error:", error);
        const status = error?.response?.status;
        const data = error?.response?.data;
        if (status === 422 && data?.limit_reached) {
          this.messages[loadingIdx] = {
            role: "assistant",
            loading: false,
            content: "",
            parsed: {
              type: "limit_reached",
              explanation:
                data.error || this.$t("ai.widget.limitReachedMessage"),
              messages_count: data.messages_count,
              messages_limit: data.messages_limit,
            },
          };
        } else {
          const errText = this.$t("ai.widget.errorMessage");
          this.messages[loadingIdx] = {
            role: "assistant",
            loading: false,
            content: errText,
            parsed: { type: "text", explanation: errText },
          };
        }
      } finally {
        this.isLoading = false;
        this.scrollToBottom();
      }
    },

    handleRedirection(usage) {
      // console.log("usage ==> ", usage);
      this.$store.dispatch("pbxElements", null);
      const toolsUsed = usage?.tools_used;
      // console.log("toolsUsed ==> ", toolsUsed);
      if (!Array.isArray(toolsUsed) || toolsUsed.length === 0) return;
      const match = toolsUsed.find((t) => redirectionUrls[t.tool]);
      if (!match) return;

      // Dispatch element type so destination view's mounted() check doesn't clear pbxElements
      if (toolElementTypes[match.tool]) {
        this.$store.dispatch("pbxElementType", toolElementTypes[match.tool]);
      }

      // Guard: params can be [] for tools like get_pbx_map
      const params =
        match.params && !Array.isArray(match.params) ? match.params : {};
      // console.log("match ==> ", match);

      // Set date range in Vuex so HoverDatePicker picks it up on mount
      if (params.start_date && params.end_date) {
        this.$store.dispatch("pbxCustomDate", {
          start: new Date(params.start_date),
          end: new Date(params.end_date),
        });
        this.$store.dispatch("pbxPeriodSelected", "custom");
      }

      // Set time range if provided
      if (params.start_time && params.end_time) {
        this.$store.dispatch("pbxTimeSelected", [
          params.start_time,
          params.end_time,
        ]);
      }

      // Collect elements to dispatch — only pick fields that match the tool's element type
      // to avoid dispatching queue params for extension tools and vice-versa.
      const elementFieldsByType = {
        extension: ["dns", "extensions", "extension_ids", "agents", "elements"],
        queue: ["dns", "queues", "queue_ids", "elements"],
        caller: ["callers", "dids", "elements"],
      };
      const elementType = toolElementTypes[match.tool];
      const elementFields = elementFieldsByType[elementType] || [
        "dns",
        "queues",
        "extensions",
        "dids",
        "callers",
        "agents",
        "queue_ids",
        "extension_ids",
        "elements",
      ];
      let elements = null;
      for (const field of elementFields) {
        if (Array.isArray(params[field]) && params[field].length > 0) {
          elements = params[field];
          break;
        }
      }

      // Dispatch elements AFTER navigation so the urlType watcher (which clears
      // pbxElements on route change) fires first on null, then the view reacts
      // to the freshly dispatched elements.
      this.$router.push({ path: "/expert-statistics/home/intro" }).then(() => {
        this.$router
          .push({ path: redirectionUrls[match.tool] })
          .catch(() => {})
          .then(() => {
            if (elements) {
              this.$store.dispatch("pbxElements", elements);
            }
          })
          .finally(() => this.$forceUpdate());
      });
    },

    openReportForm(msg) {
      const params = msg.parsed?.suggest_report?.params || {};
      this.reportFormData = {
        hintText: msg.parsed?.suggest_report?.text || "",
        title: msg.parsed?.title || "",
        email: "",
        language: this.language || "fr",
        period_type: params.period_type || "week",
        period_start: params.period_start || "",
        period_end: params.period_end || "",
        schedule_type: params.schedule_type || "none",
        schedule_day_of_week: params.schedule_day_of_week || null,
        schedule_day_of_month: params.schedule_day_of_month || null,
        schedule_time: params.schedule_time || "08:00",
        schedule_cron: params.schedule_cron || null,
        send_now: false,
      };
      this.reportFormError = null;
      this.reportFormSuccess = false;
      this.reportFormOpen = true;
    },

    closeReportForm() {
      this.reportFormOpen = false;
      this.reportFormError = null;
      this.reportFormSuccess = false;
    },

    async submitReport(formData) {
      this.reportFormError = null;
      const f = formData;
      if (!f.title?.trim()) {
        this.reportFormError = this.$t("ai.widget.report.errorTitle");
        return;
      }
      if (!f.email?.trim()) {
        this.reportFormError = this.$t("ai.widget.report.errorEmail");
        return;
      }
      this.reportFormSubmitting = true;
      try {
        await axios.post(
          `${this.apiBaseUrl}/api/v1.2/${this.hostName}/ai/reports`,
          {
            conversation_id: this.conversationId,
            title: f.title,
            email: f.email,
            language: f.language,
            period_type: f.period_type,
            period_start: f.period_start || undefined,
            period_end: f.period_end || undefined,
            schedule_type: f.schedule_type,
            schedule_day_of_week: f.schedule_day_of_week || undefined,
            schedule_day_of_month: f.schedule_day_of_month || undefined,
            schedule_time: f.schedule_time || undefined,
            schedule_cron: f.schedule_cron || undefined,
            send_now: f.send_now || undefined,
          },
          { headers: { Authorization: `Bearer ${this.bearerToken}` } },
        );
        this.reportFormSuccess = true;
        this.fetchReports();
        setTimeout(() => this.closeReportForm(), 2000);
      } catch (err) {
        this.reportFormError =
          err?.response?.data?.message ||
          this.$t("ai.widget.report.errorGeneric");
      } finally {
        this.reportFormSubmitting = false;
      }
    },

    newConversation() {
      this.messages = [];
      this.conversationId = null;
      this.view = "chat";
      this.$nextTick(() => this.$refs.chatInput?.focus());
    },

    async toggleHistory() {
      if (this.view === "history") {
        this.view = "chat";
        return;
      }
      this.view = "history";
      await this.fetchHistory();
    },

    async fetchHistory() {
      this.historyLoading = true;
      try {
        const resp = await axios.get(
          `${this.apiBaseUrl}/api/v1.2/${this.hostName}/ai/chat/history`,
          { headers: { Authorization: `Bearer ${this.bearerToken}` } },
        );
        const data = resp.data;
        console.log("data conversation ==> ", data);
        const list = Array.isArray(data)
          ? data
          : data.conversations || data.data || [];
        this.conversations = list.slice().sort((a, b) => {
          const ta = new Date(a.updated_at || a.created_at || 0).getTime();
          const tb = new Date(b.updated_at || b.created_at || 0).getTime();
          return tb - ta;
        });
      } catch (e) {
        console.error("History fetch error:", e);
        this.conversations = [];
      } finally {
        this.historyLoading = false;
      }
    },

    loadConversation(conv) {
      this.conversationId = conv.uuid;
      let rawMessages = conv.messages || [];
      if (typeof rawMessages === "string") {
        try {
          rawMessages = JSON.parse(rawMessages);
        } catch {
          rawMessages = [];
        }
      }
      if (!Array.isArray(rawMessages)) rawMessages = [];
      this.messages = rawMessages.map((m) => {
        if (m.role === "assistant") {
          let parsed = null;
          try {
            parsed =
              typeof m.content === "string" ? JSON.parse(m.content) : m.content;
          } catch {
            parsed = { type: "text", explanation: m.content };
          }
          return {
            role: "assistant",
            loading: false,
            content: m.content,
            parsed,
          };
        }
        return { role: "user", content: m.content };
      });
      console.log("this.messages ==> ", this.messages);
      this.view = "chat";
      this.scrollToBottom();
    },

    async deleteConversation(uuid) {
      if (!uuid) return;
      try {
        await axios.delete(
          `${this.apiBaseUrl}/api/v1.2/${this.hostName}/ai/chat/history`,
          {
            headers: { Authorization: `Bearer ${this.bearerToken}` },
            params: { uuid },
          },
        );
        this.conversations = this.conversations.filter((c) => c.uuid !== uuid);
        if (this.conversationId === uuid) {
          this.messages = [];
          this.conversationId = null;
        }
      } catch (e) {
        console.error("Delete history error:", e);
      }
    },

    async fetchReports() {
      this.reportsLoading = true;
      this.reportDeleteConfirm = null;
      try {
        const { data } = await axios.get(
          `${this.apiBaseUrl}/api/v1.2/${this.hostName}/ai/reports`,
          { headers: { Authorization: `Bearer ${this.bearerToken}` } },
        );
        console.log("data reports ai ==> ", data);
        this.reports = data;
      } catch (e) {
        console.error("fetchReports error", e);
      } finally {
        this.reportsLoading = false;
      }
    },

    async deleteReport(uuid) {
      this.reportDeleteConfirm = null;
      try {
        await axios.delete(
          `${this.apiBaseUrl}/api/v1.2/${this.hostName}/ai/reports/${uuid}`,
          { headers: { Authorization: `Bearer ${this.bearerToken}` } },
        );
        this.reports = this.reports.filter((r) => r.uuid !== uuid);
      } catch (e) {
        console.error("deleteReport error", e);
      }
    },

    async fetchAlertCount() {
      try {
        const resp = await axios.get(
          `${this.apiBaseUrl}/api/v1.2/${this.hostName}/ai/alerts`,
          {
            params: { dismissed: false },
            headers: { Authorization: `Bearer ${this.bearerToken}` },
          },
        );
        this.unreadAlerts = resp.data?.summary?.total || 0;
      } catch {
        // Silently fail
      }
    },

    openWithQuery(query) {
      this.isOpen = true;
      this.tab = "chat";
      this.view = "chat";
      this.$nextTick(() => this.sendMessage(query));
    },

    openOnTab(tab) {
      this.isOpen = true;
      this.$nextTick(() => {
        this.tab = tab;
      });
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messagesContainer;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },

  mounted() {
    this.fetchAlertCount();
    this._alertInterval = setInterval(() => this.fetchAlertCount(), 300000);
  },

  beforeUnmount() {
    clearInterval(this._alertInterval);
  },

  watch: {
    isOpen(val) {
      if (val) {
        this.tab = "chat";
        this.view = "chat";
        this.$nextTick(() => this.$refs.chatInput?.focus());
      }
    },
    tab(val) {
      if (val === "reports") this.fetchReports();
    },
  },
};
</script>

<style scoped>
.ai-toggle {
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.45);
}
.ai-toggle:hover {
  box-shadow: 0 6px 24px rgba(99, 102, 241, 0.55);
}
.ai-panel {
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.18);
}

.chat-scroll {
  scroll-behavior: smooth;
}
.chat-scroll::-webkit-scrollbar {
  width: 4px;
}
.chat-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.chat-scroll::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 4px;
}
</style>
